import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit2, Trash2, ChevronLeft, Loader2, AlertCircle, DollarSign, FileText, Building2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';

const ACCOUNT_TYPES = [
  { value: 'ASSET', label: 'Activo', color: 'bg-blue-100 text-blue-800' },
  { value: 'LIABILITY', label: 'Pasivo', color: 'bg-red-100 text-red-800' },
  { value: 'EQUITY', label: 'Capital', color: 'bg-purple-100 text-purple-800' },
  { value: 'INCOME', label: 'Ingreso', color: 'bg-green-100 text-green-800' },
  { value: 'EXPENSE', label: 'Gasto', color: 'bg-orange-100 text-orange-800' },
] as const;

type AccountType = typeof ACCOUNT_TYPES[number]['value'];

interface Empresa {
  id: number;
  profileName: string;
  legalName: string;
  rfc: string | null;
  isDefault: boolean;
}

interface Account {
  id: number;
  code: string;
  name: string;
  type: AccountType;
  parentId: number | null;
  empresaId: number | null;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  balance: number;
  parent?: {
    id: number;
    code: string;
    name: string;
  } | null;
}

const accountFormSchema = z.object({
  code: z.string().min(1, 'El código es requerido').max(50, 'Máximo 50 caracteres'),
  name: z.string().min(1, 'El nombre es requerido').max(255, 'Máximo 255 caracteres'),
  type: z.enum(['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'], {
    required_error: 'Seleccione un tipo de cuenta',
  }),
  parentId: z.number().nullable().optional(),
  empresaId: z.number().nullable().optional(),
  description: z.string().max(500).nullable().optional(),
  isActive: z.boolean().optional().default(true),
});

type AccountFormData = z.infer<typeof accountFormSchema>;

async function fetchEmpresas(): Promise<Empresa[]> {
  const res = await fetch('/api/finance/emisores', { credentials: 'include' });
  if (!res.ok) return [];
  return res.json();
}

async function fetchAccounts(empresaId?: number | null): Promise<Account[]> {
  const url = empresaId ? `/api/finance/accounts?empresaId=${empresaId}` : '/api/finance/accounts';
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al cargar las cuentas');
  }
  return response.json();
}

async function createAccount(data: AccountFormData): Promise<Account> {
  const response = await fetch('/api/finance/accounts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al crear la cuenta');
  }
  return response.json();
}

async function updateAccount(id: number, data: Partial<AccountFormData>): Promise<Account> {
  const response = await fetch(`/api/finance/accounts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al actualizar la cuenta');
  }
  return response.json();
}

async function deleteAccount(id: number): Promise<void> {
  const response = await fetch(`/api/finance/accounts/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al eliminar la cuenta');
  }
}

export default function ChartOfAccounts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [deleteConfirmAccount, setDeleteConfirmAccount] = useState<Account | null>(null);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('');

  const empresaParam = searchParams.get('empresa');
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(
    empresaParam ? parseInt(empresaParam) : null
  );

  useEffect(() => {
    const p = searchParams.get('empresa');
    setSelectedEmpresaId(p ? parseInt(p) : null);
  }, [empresaParam]);

  const handleSetEmpresa = (id: number | null) => {
    setSelectedEmpresaId(id);
    if (id) {
      setSearchParams({ empresa: String(id) });
    } else {
      setSearchParams({});
    }
  };

  const { data: empresas = [] } = useQuery({
    queryKey: ['finance-emisores'],
    queryFn: fetchEmpresas,
  });

  const { data: rawAccounts = [], isLoading, error } = useQuery({
    queryKey: ['finance-accounts', selectedEmpresaId],
    queryFn: () => fetchAccounts(selectedEmpresaId),
  });

  const accounts = selectedTypeFilter
    ? rawAccounts.filter(a => a.type === selectedTypeFilter)
    : rawAccounts;

  const createMutation = useMutation({
    mutationFn: createAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-accounts'] });
      toast({
        title: 'Cuenta creada',
        description: 'La cuenta contable se ha creado exitosamente.',
      });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AccountFormData> }) =>
      updateAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-accounts'] });
      toast({
        title: 'Cuenta actualizada',
        description: 'La cuenta contable se ha actualizado exitosamente.',
      });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-accounts'] });
      toast({
        title: 'Cuenta eliminada',
        description: 'La cuenta contable se ha eliminado exitosamente.',
      });
      setDeleteConfirmAccount(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      code: '',
      name: '',
      type: 'ASSET',
      parentId: null,
      description: '',
      isActive: true,
    },
  });

  const handleOpenDialog = (account?: Account) => {
    if (account) {
      setEditingAccount(account);
      form.reset({
        code: account.code,
        name: account.name,
        type: account.type,
        parentId: account.parentId,
        empresaId: account.empresaId,
        description: account.description || '',
        isActive: account.isActive,
      });
    } else {
      setEditingAccount(null);
      form.reset({
        code: '',
        name: '',
        type: 'ASSET',
        parentId: null,
        empresaId: selectedEmpresaId,
        description: '',
        isActive: true,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAccount(null);
    form.reset();
  };

  const onSubmit = (data: AccountFormData) => {
    if (editingAccount) {
      updateMutation.mutate({ id: editingAccount.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const getAccountTypeInfo = (type: AccountType) => {
    return ACCOUNT_TYPES.find((t) => t.value === type) || ACCOUNT_TYPES[0];
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/app/finance')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <DollarSign className="w-7 h-7 text-green-600" />
                Catálogo de Cuentas
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {selectedEmpresaId && empresas.find(e => e.id === selectedEmpresaId)
                  ? `Empresa: ${empresas.find(e => e.id === selectedEmpresaId)?.profileName || empresas.find(e => e.id === selectedEmpresaId)?.legalName}`
                  : 'Vista global del plan contable'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/app/finance/new-entry')} className="gap-2">
              <FileText className="w-4 h-4" />
              Nueva Póliza
            </Button>
            <Button onClick={() => handleOpenDialog()} className="gap-2">
              <Plus className="w-4 h-4" />
              Nueva Cuenta
            </Button>
          </div>
        </div>

        {/* Filter Bar: Empresa + Tipo */}
        <div className="bg-white rounded-lg shadow-sm border p-3 mb-4 flex flex-col gap-3">
          {empresas.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <Building2 className="w-4 h-4 text-indigo-500 shrink-0" />
              <span className="text-sm font-medium text-gray-700 shrink-0">Empresa:</span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSetEmpresa(null)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedEmpresaId === null ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Todas
                </button>
                {empresas.map((emp) => (
                  <button
                    key={emp.id}
                    onClick={() => handleSetEmpresa(emp.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedEmpresaId === emp.id ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {emp.profileName || emp.legalName}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-700 shrink-0 pl-0.5">Tipo:</span>
            <div className="flex flex-wrap gap-2">
              {[
                { value: '', label: 'Todos' },
                { value: 'ASSET', label: 'Activo' },
                { value: 'LIABILITY', label: 'Pasivo' },
                { value: 'EQUITY', label: 'Capital' },
                { value: 'INCOME', label: 'Ingreso' },
                { value: 'EXPENSE', label: 'Gasto' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedTypeFilter(opt.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedTypeFilter === opt.value ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-red-500">
              <AlertCircle className="w-8 h-8 mb-2" />
              <p>{(error as Error).message}</p>
            </div>
          ) : accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <DollarSign className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No hay cuentas contables</p>
              <p className="text-sm mt-1">Crea tu primera cuenta para comenzar</p>
              <Button onClick={() => handleOpenDialog()} className="mt-4 gap-2">
                <Plus className="w-4 h-4" />
                Nueva Cuenta
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead className="w-[120px]">Tipo</TableHead>
                    <TableHead className="w-[150px] text-right">Saldo</TableHead>
                    <TableHead className="w-[100px]">Estado</TableHead>
                    <TableHead className="w-[100px] text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((account) => {
                    const typeInfo = getAccountTypeInfo(account.type);
                    return (
                      <TableRow key={account.id}>
                        <TableCell className="font-mono font-medium">
                          {account.code}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{account.name}</p>
                            {account.parent && (
                              <p className="text-xs text-gray-500">
                                ↳ {account.parent.code} - {account.parent.name}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.color}`}
                          >
                            {typeInfo.label}
                          </span>
                        </TableCell>
                        <TableCell className={`text-right font-mono ${account.balance < 0 ? 'text-red-600' : account.balance > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                          {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(account.balance || 0)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              account.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {account.isActive ? 'Activa' : 'Inactiva'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleOpenDialog(account)}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4 text-gray-500" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirmAccount(account)}
                              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
          {ACCOUNT_TYPES.map((type) => {
            const count = accounts.filter((a) => a.type === type.value).length;
            return (
              <div
                key={type.value}
                className="bg-white rounded-lg border p-4 text-center"
              >
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <p className="text-sm text-gray-500">{type.label}s</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? 'Editar Cuenta' : 'Nueva Cuenta Contable'}
            </DialogTitle>
            <DialogDescription>
              {editingAccount
                ? 'Modifica los datos de la cuenta contable'
                : 'Completa los datos para crear una nueva cuenta'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  placeholder="Ej: 1100"
                  {...form.register('code')}
                  className={form.formState.errors.code ? 'border-red-500' : ''}
                />
                {form.formState.errors.code && (
                  <p className="text-xs text-red-500">
                    {form.formState.errors.code.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Tipo *</Label>
                <Select
                  value={form.watch('type')}
                  onValueChange={(value: AccountType) => form.setValue('type', value)}
                >
                  <SelectTrigger
                    className={form.formState.errors.type ? 'border-red-500' : ''}
                  >
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.type && (
                  <p className="text-xs text-red-500">
                    {form.formState.errors.type.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                placeholder="Ej: Bancos"
                {...form.register('name')}
                className={form.formState.errors.name ? 'border-red-500' : ''}
              />
              {form.formState.errors.name && (
                <p className="text-xs text-red-500">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="parentId">Cuenta Padre (opcional)</Label>
              <Select
                value={form.watch('parentId')?.toString() || 'none'}
                onValueChange={(value) =>
                  form.setValue('parentId', value === 'none' ? null : parseInt(value))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin cuenta padre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin cuenta padre</SelectItem>
                  {accounts
                    .filter((a) => a.id !== editingAccount?.id)
                    .map((account) => (
                      <SelectItem key={account.id} value={account.id.toString()}>
                        {account.code} - {account.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {empresas.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="empresaId">Empresa (opcional)</Label>
                <Select
                  value={form.watch('empresaId')?.toString() || 'none'}
                  onValueChange={(value) =>
                    form.setValue('empresaId', value === 'none' ? null : parseInt(value))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin empresa (global)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin empresa (global al tenant)</SelectItem>
                    {empresas.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id.toString()}>
                        {emp.profileName || emp.legalName}{emp.rfc ? ` (${emp.rfc})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Asigna esta cuenta al plan contable de una empresa específica.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Descripción (opcional)</Label>
              <Input
                id="description"
                placeholder="Descripción de la cuenta"
                {...form.register('description')}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                disabled={isMutating}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isMutating}>
                {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingAccount ? 'Guardar Cambios' : 'Crear Cuenta'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmAccount}
        onOpenChange={() => setDeleteConfirmAccount(null)}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Eliminar Cuenta</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar la cuenta{' '}
              <strong>
                {deleteConfirmAccount?.code} - {deleteConfirmAccount?.name}
              </strong>
              ? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmAccount(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deleteConfirmAccount && deleteMutation.mutate(deleteConfirmAccount.id)
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
